import PortalIcon from "../ui/PortalIcon";

const ORBIT_ITEMS = [
  ["file", "Test Papers"],
  ["dashboard", "PPT Slides"],
  ["info", "MCQ Bank"],
  ["image", "Thumbnails"],
  ["folder", "Materials"],
  ["school", "Courses"],
  ["edit", "PDF Editing"],
  ["keyboard", "Typing"],
];

export default function HeroOrbitVisual({ t }) {
  const isDark = t.bg === '#070B14';
  return (
    <div className="hero-orbit-wrap fade-up" aria-label="DG HelpMate service orbit visual">
      <style>{`
        @keyframes dghOrbitSpin { to { transform: translate(-50%, -50%) rotate(360deg); } }
        @keyframes dghOrbitSpinSoft { to { transform: rotate(360deg); } }
        @keyframes dghOrbitSpinReverse { to { transform: rotate(-360deg); } }
        @keyframes dghOrbitCardOrbit {
          from {
            transform: translate(-50%, -50%) rotate(var(--angle)) translateY(calc(var(--orbit-radius) * -1)) rotate(calc(var(--angle) * -1));
          }
          to {
            transform: translate(-50%, -50%) rotate(calc(var(--angle) + 360deg)) translateY(calc(var(--orbit-radius) * -1)) rotate(calc((var(--angle) + 360deg) * -1));
          }
        }
        @keyframes dghOrbitFloat {
          0%,100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes dghOrbitPulse {
          0%,100% { opacity:.45; transform: scale(.96); }
          50% { opacity:.9; transform: scale(1.04); }
        }
        @keyframes dghOrbitGlow {
          0%,100% { opacity:.42; filter: blur(18px); transform: translate(-50%, -50%) scale(.98); }
          50% { opacity:.82; filter: blur(24px); transform: translate(-50%, -50%) scale(1.04); }
        }
        .hero-orbit-wrap {
          min-width: 0;
          min-height: clamp(410px, 55vw, 640px);
          display: grid;
          place-items: center;
          position: relative;
          animation-delay: .5s;
          perspective: 1100px;
          overflow: hidden;
          max-width: 100%;
        }
        .hero-orbit-shell {
          width: min(100%, 590px);
          aspect-ratio: 1;
          position: relative;
          border-radius: 50%;
          --orbit-radius: clamp(176px, 18.8vw, 242px);
          background: ${isDark
            ? `radial-gradient(circle at 50% 50%, rgba(248,250,252,.12), transparent 12%),
               radial-gradient(circle at 50% 50%, rgba(245,158,11,.22), transparent 28%),
               radial-gradient(circle at 50% 50%, rgba(249,115,22,.12), transparent 52%),
               radial-gradient(circle at 50% 50%, rgba(14,165,233,.08), transparent 68%)`
            : `radial-gradient(circle at 50% 50%, rgba(245,158,11,.08), transparent 48%)`
          };
          filter: ${isDark ? 'drop-shadow(0 34px 80px rgba(0,0,0,.42))' : 'drop-shadow(0 8px 24px rgba(15,23,42,.08))'};
          isolation: isolate;
        }
        .hero-orbit-shell::before,
        .hero-orbit-shell::after {
          content: "";
          position: absolute;
          inset: 5%;
          border-radius: 50%;
          border: 1px dotted rgba(245,158,11,.28);
          box-shadow: inset 0 0 40px rgba(245,158,11,.10), 0 0 56px rgba(245,158,11,.10);
        }
        .hero-orbit-shell::after {
          inset: 18%;
          border-style: dashed;
          border-color: rgba(255,255,255,.17);
          animation: dghOrbitSpinReverse 28s linear infinite;
        }
        .hero-orbit-ambient {
          position: absolute;
          left: 50%;
          top: 50%;
          width: 52%;
          aspect-ratio: 1;
          border-radius: 50%;
          transform: translate(-50%, -50%);
          background: ${isDark
            ? 'radial-gradient(circle, rgba(245,158,11,.38), rgba(249,115,22,.15) 32%, transparent 68%)'
            : 'radial-gradient(circle, rgba(245,158,11,.18), rgba(249,115,22,.06) 40%, transparent 70%)'
          };
          animation: dghOrbitGlow 4.8s ease-in-out infinite;
          z-index: -1;
        }
        .hero-orbit-ring {
          position: absolute;
          inset: 10%;
          border-radius: 50%;
          background:
            conic-gradient(from 30deg, transparent 0 9%, rgba(245,158,11,.95) 10% 13%, transparent 15% 34%, rgba(249,115,22,.85) 35% 38%, transparent 40% 62%, rgba(245,158,11,.72) 64% 67%, transparent 69% 100%);
          mask: radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 1px));
          animation: dghOrbitSpinSoft 18s linear infinite;
          filter: drop-shadow(0 0 12px rgba(245,158,11,.45));
        }
        .hero-orbit-ring.second {
          inset: 1%;
          background:
            conic-gradient(from 120deg, transparent 0 15%, rgba(255,255,255,.34) 16% 17%, transparent 18% 44%, rgba(245,158,11,.44) 45% 46%, transparent 47% 74%, rgba(255,255,255,.26) 75% 76%, transparent 77% 100%);
          animation-duration: 34s;
          animation-direction: reverse;
        }
        .hero-orbit-track {
          position: absolute;
          left: 50%;
          top: 50%;
          width: var(--orbit-size);
          aspect-ratio: 1;
          border-radius: 50%;
          transform: translate(-50%, -50%);
          border: 1px solid rgba(245,158,11,.34);
          box-shadow:
            0 0 44px rgba(245,158,11,.12),
            inset 0 0 36px rgba(245,158,11,.07);
          --orbit-size: min(82%, 484px);
        }
        .hero-orbit-track::before,
        .hero-orbit-track::after {
          content: "";
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
        }
        .hero-orbit-track::before {
          inset: -15%;
          border: 1px dotted rgba(245,158,11,.18);
        }
        .hero-orbit-track::after {
          inset: 18%;
          border: 1px solid rgba(255,255,255,.11);
          box-shadow: 0 0 28px rgba(255,255,255,.04) inset;
        }
        .hero-orbit-card {
          position: absolute;
          left: 50%;
          top: 50%;
          width: clamp(106px, 13vw, 128px);
          min-height: clamp(92px, 10vw, 112px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 11px 10px;
          border-radius: 20px;
          border: 1px solid ${isDark ? 'rgba(255,255,255,.14)' : 'rgba(245,158,11,.28)'};
          background: ${isDark
            ? `linear-gradient(145deg, rgba(255,255,255,.12), rgba(255,255,255,.035)),
               linear-gradient(160deg, rgba(12,16,28,.96), rgba(21,24,34,.9) 52%, rgba(31,20,9,.92))`
            : `linear-gradient(145deg, rgba(255,255,255,.95), rgba(255,255,255,.85)),
               ${t.bgCard}`
          };
          color: ${isDark ? '#F8FAFC' : t.text};
          box-shadow: ${isDark
            ? '0 18px 42px rgba(0,0,0,.38), 0 0 0 1px rgba(255,255,255,.06) inset, 0 0 32px rgba(245,158,11,.10)'
            : '0 6px 20px rgba(15,23,42,.10), 0 0 0 1px rgba(245,158,11,.18) inset'
          };
          transform: translate(-50%, -50%) rotate(var(--angle)) translateY(calc(var(--orbit-radius) * -1)) rotate(calc(var(--angle) * -1));
          animation: dghOrbitCardOrbit 36s linear infinite;
          backdrop-filter: blur(18px) saturate(130%);
          transition: box-shadow .25s ease, border-color .25s ease, filter .25s ease;
          will-change: transform;
          overflow: hidden;
        }
        .hero-orbit-card::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background:
            radial-gradient(circle at 24% 16%, rgba(255,255,255,.28), transparent 32%),
            linear-gradient(135deg, rgba(245,158,11,.22), transparent 34%, rgba(14,165,233,.10));
          opacity: .66;
          pointer-events: none;
        }
        .hero-orbit-card-inner {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          gap: 9px;
          width: 100%;
          animation: dghOrbitFloat 4.8s ease-in-out var(--delay) infinite;
        }
        .hero-orbit-card:hover {
          border-color: rgba(245,158,11,.64);
          box-shadow:
            0 24px 56px rgba(245,158,11,.22),
            0 0 0 1px rgba(255,255,255,.10) inset,
            0 0 34px rgba(245,158,11,.16);
          filter: brightness(1.08);
        }
        .hero-orbit-icon {
          width: 42px;
          height: 42px;
          border-radius: 14px;
          display: grid;
          place-items: center;
          color: #F8FAFC;
          background:
            linear-gradient(145deg, rgba(255,255,255,.18), rgba(255,255,255,.05)),
            linear-gradient(135deg,#F59E0B,#F97316);
          flex: 0 0 auto;
          box-shadow: 0 12px 26px rgba(245,158,11,.34), 0 0 0 1px rgba(255,255,255,.18) inset;
        }
        .hero-orbit-label {
          font-size: clamp(.72rem, 1.25vw, .84rem);
          font-weight: 900;
          line-height: 1.15;
          letter-spacing: 0;
          text-shadow: ${isDark ? '0 1px 12px rgba(0,0,0,.62)' : 'none'};
        }
        .hero-orbit-center {
          position: absolute;
          left: 50%;
          top: 50%;
          width: clamp(184px, 24vw, 248px);
          transform: translate(-50%, -50%);
          border-radius: 50%;
          aspect-ratio: 1;
          padding: clamp(20px, 3vw, 28px);
          text-align: center;
          display: grid;
          place-items: center;
          background: ${isDark
            ? `radial-gradient(circle at 50% 18%, rgba(255,255,255,.14), transparent 34%),
               linear-gradient(160deg, rgba(7,11,20,.96), rgba(16,22,34,.94) 48%, rgba(27,20,12,.96))`
            : `radial-gradient(circle at 50% 18%, rgba(255,255,255,.9), rgba(255,255,255,.7) 34%),
               linear-gradient(160deg, ${t.bgCard}, ${t.bgCard2 || t.bgCard})`
          };
          border: 1px solid rgba(245,158,11,.34);
          box-shadow: ${isDark
            ? '0 32px 78px rgba(0,0,0,.48), 0 0 68px rgba(245,158,11,.22), 0 0 0 1px rgba(255,255,255,.06) inset'
            : '0 8px 32px rgba(15,23,42,.12), 0 0 32px rgba(245,158,11,.16), 0 0 0 1px rgba(245,158,11,.16) inset'
          };
          z-index: 2;
        }
        .hero-orbit-center::before {
          content: "";
          position: absolute;
          inset: -18px;
          border-radius: 50%;
          border: 2px solid rgba(245,158,11,.82);
          box-shadow: 0 0 24px rgba(245,158,11,.42), inset 0 0 24px rgba(245,158,11,.16);
        }
        .hero-orbit-center-inner {
          position: relative;
          z-index: 1;
          display: grid;
          justify-items: center;
        }
        .hero-orbit-logo {
          width: clamp(70px, 9vw, 92px);
          height: clamp(70px, 9vw, 92px);
          border-radius: 24px;
          object-fit: contain;
          margin-bottom: 14px;
          background: rgba(255,255,255,.96);
          box-shadow: 0 16px 34px rgba(245,158,11,.30), 0 0 0 1px rgba(255,255,255,.55) inset;
        }
        .hero-orbit-title {
          color: ${isDark ? '#F8FAFC' : t.text};
          font-weight: 950;
          font-size: clamp(1.08rem, 2vw, 1.38rem);
          line-height: 1.15;
        }
        .hero-orbit-title span { color: #F59E0B; }
        .hero-orbit-url {
          margin-top: 7px;
          color: ${isDark ? 'rgba(226,232,240,.74)' : t.muted};
          font-size: clamp(.72rem, 1.4vw, .82rem);
          font-weight: 850;
        }
        .hero-orbit-dot {
          position: absolute;
          width: 9px;
          height: 9px;
          border-radius: 50%;
          background: #F59E0B;
          box-shadow: 0 0 24px #F59E0B;
          animation: dghOrbitPulse 2.8s ease-in-out infinite;
        }
        .hero-orbit-dot.one { left: 17%; top: 24%; }
        .hero-orbit-dot.two { right: 12%; top: 39%; animation-delay: .7s; }
        .hero-orbit-dot.three { left: 27%; bottom: 13%; animation-delay: 1.2s; }
        .hero-orbit-spark {
          position: absolute;
          left: 50%;
          top: 50%;
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #FBBF24;
          box-shadow: 0 0 20px #F59E0B, 0 0 42px rgba(245,158,11,.55);
          transform: translate(-50%, -50%) rotate(var(--spark-angle)) translateY(calc(var(--spark-radius) * -1));
          animation: dghOrbitPulse 2.6s ease-in-out var(--spark-delay) infinite;
        }
        @media (max-width: 768px) {
          .hero-orbit-wrap {
            min-height: 440px;
            margin-top: 4px;
            order: 2;
            width: 100%;
            overflow: hidden;
          }
          .hero-orbit-shell {
            width: min(96vw, 430px);
            --orbit-radius: clamp(146px, 39vw, 178px);
          }
          .hero-orbit-track { --orbit-size: min(82%, 348px); }
          .hero-orbit-card {
            width: 96px;
            min-height: 86px;
            padding: 9px 8px;
            border-radius: 17px;
          }
          .hero-orbit-icon {
            width: 34px;
            height: 34px;
            border-radius: 12px;
          }
          .hero-orbit-center {
            width: 166px;
            padding: 18px;
          }
        }
        @media (max-width: 420px) {
          .hero-orbit-wrap { min-height: 390px; overflow: hidden; width: 100%; }
          .hero-orbit-shell {
            width: min(90vw, 340px);
            --orbit-radius: clamp(110px, 34vw, 140px);
          }
          .hero-orbit-track { --orbit-size: min(80%, 272px); }
          .hero-orbit-card { width: 80px; min-height: 72px; padding: 8px 6px; }
          .hero-orbit-icon { width: 30px; height: 30px; border-radius: 10px; }
          .hero-orbit-label { font-size: .64rem; }
          .hero-orbit-center { width: 138px; padding: 15px; }
          .hero-orbit-logo { width: 54px; height: 54px; border-radius: 18px; margin-bottom: 10px; }
          .hero-orbit-title { font-size: .96rem; }
          .hero-orbit-url { font-size: .68rem; }
        }
      `}</style>

      <div className="hero-orbit-shell">
        <div className="hero-orbit-ambient" />
        <div className="hero-orbit-ring" />
        <div className="hero-orbit-ring second" />
        <span className="hero-orbit-dot one" />
        <span className="hero-orbit-dot two" />
        <span className="hero-orbit-dot three" />
        {[18, 96, 156, 236, 306].map((angle, index) => (
          <span
            className="hero-orbit-spark"
            key={angle}
            style={{
              "--spark-angle": `${angle}deg`,
              "--spark-radius": `${index % 2 ? 45 : 38}%`,
              "--spark-delay": `${index * 0.28}s`,
            }}
          />
        ))}

        <div className="hero-orbit-center">
          <div className="hero-orbit-center-inner">
            <img className="hero-orbit-logo" src="/images/logo.png" alt="DG HelpMate" />
            <div className="hero-orbit-title">DG <span>HelpMate</span></div>
            <div className="hero-orbit-url">dghelpmate.com</div>
          </div>
        </div>

        <div className="hero-orbit-track">
          {ORBIT_ITEMS.map(([icon, label], index) => {
            const angle = index * 45;
            return (
              <div
                className="hero-orbit-card"
                key={label}
                style={{
                  "--angle": `${angle}deg`,
                  "--delay": `${index * 0.18}s`,
                }}
              >
                <span className="hero-orbit-card-inner">
                  <span className="hero-orbit-icon">
                    <PortalIcon name={icon} size={19} stroke={2.4} />
                  </span>
                  <span className="hero-orbit-label">{label}</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
